import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import type { Application } from "./application"
import { useDocumentInstanceLifetime } from "./document-instance"
import { useApplication, useApplicationScope } from "./application-provider"
import type { ApplicationScope } from "./plugin-runtime"

type Cleanup = () => void | Promise<void>

export interface UseCaseContext extends ApplicationScope {
  readonly application: Application
  readonly featureId: string | null
  readonly signal: AbortSignal
  /** Register resources as acquired, including after an awaited operation. */
  onDispose(cleanup: Cleanup): void
}

export interface UseCaseDefinition<TState> {
  readonly start?: (context: UseCaseContext) => void | Promise<void>
  /** The feature selects typed policies/userState; the framework owns their lifetime. */
  readonly load: (context: UseCaseContext) => TState | Promise<TState>
}

export function defineUseCase<TState>(definition: UseCaseDefinition<TState>): UseCaseDefinition<TState> {
  return Object.freeze({ ...definition })
}

type UseCaseState<TState> =
  | { readonly status: "pending"; readonly data: undefined; readonly error: undefined }
  | { readonly status: "ready"; readonly data: TState; readonly error: undefined }
  | { readonly status: "error"; readonly data: undefined; readonly error: unknown }

export type UseCase<TState> = UseCaseState<TState> & {
  readonly signal: AbortSignal
  readonly retry: () => void
}

const notStarted = AbortSignal.abort(new DOMException("Use case has not started.", "AbortError"))
const pending = { status: "pending", data: undefined, error: undefined } as const

/**
 * One lifetime per mounted consumer (document, page or dialog), not per plugin.
 * A retained document owns the lifetime while React Activity disconnects hidden UI effects.
 * Keep definitions outside render. Pass a primitive instanceKey for parameter changes.
 */
export function useCase<TState>(definition: UseCaseDefinition<TState>, instanceKey?: string | number): UseCase<TState> {
  const application = useApplication()
  const scope = useApplicationScope()
  const revision = useSyncExternalStore(
    (listener) => application.session.subscribeAccessContext(listener),
    () => application.session.accessContext.revision,
    () => application.session.accessContext.revision
  )
  const documentLifetime = useDocumentInstanceLifetime()
  const [attempt, setAttempt] = useState(0)
  const identity = useMemo(
    () => ({ definition, application, scope, revision, instanceKey, attempt }),
    [definition, application, scope, revision, instanceKey, attempt]
  )
  const [snapshot, setSnapshot] = useState<{
    readonly identity: typeof identity
    readonly signal: AbortSignal
    readonly state: UseCaseState<TState>
  } | null>(null)
  const runtimeRef = useRef<{
    readonly identity: typeof identity
    readonly signal: AbortSignal
    start(): void
    dispose(): void
  } | null>(null)

  useEffect(() => {
    let runtime = runtimeRef.current
    if (runtime == null || runtime.identity !== identity) {
      if (runtime != null) {
        documentLifetime?.release(runtime)
        runtime.dispose()
      }

      const controller = new AbortController()
      const signal = controller.signal
      const cleanups: Cleanup[] = []
      let disposed = false
      let started = false
      const runCleanup = (cleanup: Cleanup) => {
        try {
          void Promise.resolve(cleanup()).catch((error) => scope.telemetry.error("Use-case disposal failed.", error))
        } catch (error) {
          scope.telemetry.error("Use-case disposal failed.", error)
        }
      }
      let unsubscribe: () => void = () => undefined
      const dispose = () => {
        if (disposed) return
        disposed = true
        unsubscribe()
        controller.abort()
        for (const cleanup of cleanups.splice(0).reverse()) runCleanup(cleanup)
      }
      unsubscribe = application.session.subscribeAccessContext(dispose)
      const context: UseCaseContext = Object.freeze({
        ...scope,
        application,
        signal,
        featureId: scope.id == null ? null : (application.getPluginAvailability(scope.id)?.featureId ?? null),
        onDispose(cleanup: Cleanup) {
          if (disposed) runCleanup(cleanup)
          else cleanups.push(cleanup)
        },
      })
      runtime = {
        identity,
        signal,
        start() {
          if (started) return
          started = true
          setSnapshot({ identity, signal, state: pending })
          // StrictMode replays effects synchronously. Never send Start for a lifetime
          // which was disposed before it actually began.
          void Promise.resolve()
            .then(async () => {
              signal.throwIfAborted()
              await definition.start?.(context)
              signal.throwIfAborted()
              const data = await definition.load(context)
              signal.throwIfAborted()
              setSnapshot({ identity, signal, state: { status: "ready", data, error: undefined } })
            })
            .catch((error) => {
              if (signal.aborted) return
              setSnapshot({ identity, signal, state: { status: "error", data: undefined, error } })
              dispose()
            })
        },
        dispose,
      }
      runtimeRef.current = runtime
      documentLifetime?.retain(runtime, runtime.dispose)
    }

    runtime.start()
    return () => {
      if (documentLifetime != null) {
        if (!documentLifetime.releaseFromEffect(runtime)) return
      } else {
        runtime.dispose()
      }
      if (runtimeRef.current === runtime) runtimeRef.current = null
    }
  }, [identity, application, scope, definition, documentLifetime])

  const current = snapshot?.identity === identity ? snapshot : null
  return {
    ...(current?.state ?? pending),
    signal: current?.signal ?? notStarted,
    retry: () => setAttempt((value) => value + 1),
  }
}
